import { ApiError } from "@sidegigllc/api";
import type { HireReadiness, HireReadinessReasonEnum } from "@sidegigllc/api/client";

/** Why a location can't hire yet, as the API says it (`hire_readiness.reason`, and `target_value` on a refused action). */
export type HireNotReadyReason = HireReadinessReasonEnum;

/** The API's "is Hire ready, and why not" answer on a job (optional: older APIs omit it). `billing_source` isn't needed to pick a gate. */
export type HireReadinessAnswer = Pick<HireReadiness, "ready" | "reason">;

/** What a gated Hire action opens instead of running. */
export type HireGate = "addPaymentMethod" | "askOrgAdmin" | "pendingApproval";

/**
 * The one question every gated Hire action asks. Null when nothing stands in the way, including when the API
 * didn't say (an older API), so those pages behave as they did before. `canUpdatePayment` is who may add a card.
 */
export const hireGateFor = (
  readiness: HireReadinessAnswer | null | undefined,
  { canUpdatePayment }: { canUpdatePayment: boolean },
): HireGate | null => {
  if (!readiness || readiness.ready) return null;
  if (readiness.reason === "not_approved") return "pendingApproval";
  if (readiness.reason === "no_payment_method") return canUpdatePayment ? "addPaymentMethod" : "askOrgAdmin";
  return null;
};

const HIRE_NOT_READY_CODE = "hire_location_not_ready";

/** The reason on a refused Hire action, for a page that hadn't loaded `hire_readiness`. Null for any other error. */
export const hireNotReadyReasonFrom = (error: unknown): HireNotReadyReason | null => {
  const { error: data } = ApiError.fromThrown(error);
  if (data.code !== HIRE_NOT_READY_CODE) return null;
  return data.target_value === "not_approved" || data.target_value === "no_payment_method" ? data.target_value : null;
};

/** What a working-interview shift that isn't being invited yet says, or null for a ready location. */
export const workingInterviewWaitCopy = (reason: HireNotReadyReason | null | undefined) => {
  if (reason === "no_payment_method") {
    return {
      pill: "Waiting on your payment method",
      detail: "Qwick starts inviting once it's added",
      invited: "Qwick hasn't started inviting Pros",
      invitedBody: "Add a payment method and Qwick starts asking the best-matched Pros for this shift right away.",
    };
  }
  if (reason === "not_approved") {
    return {
      pill: "Waiting on Qwick's review",
      detail: "Qwick starts inviting once your location is approved",
      invited: "Qwick hasn't started inviting Pros",
      invitedBody: "Once your location is approved, Qwick starts asking the best-matched Pros for this shift.",
    };
  }
  return null;
};
